import * as yup from '@if/core/yup'
import { ControlledRadioGroup, type FormSetup } from '@if/mui'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import { useUser } from '@src/contexts/AppContext'
import { useUpdateBanner } from '@src/data/api/merchants-api/merchants-api'
import { BannerDto, BannerSchema } from '@src/data/types/BannerSchema'
import { EBannerOwnedFieldKey } from '@src/data/types/Constants'
import { Merchant } from '@src/data/types/Merchant'
import { useCallback, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CustomPromotionPanel from '../../../EditMerchant/components/CustomPromotionPanel'
import { TabFormMethods } from '../../../EditMerchant/components/tabs/MerchantGeneralTab'
import { useSyncFormDefaults } from '../../../EditMerchant/components/useSyncFormDefaults'
import { BannerTabConfig } from './BannerGeneralTab'
import { getBannerDtoFromMerchant } from './bannerDto'

// String-typed so equality checks against the string[] field don't trip the enum-comparison rule.
const CUSTOM_BUYDOWN_KEY: string = EBannerOwnedFieldKey.CustomBuydown

// Only this tab edits the rebate; the per-user cap is supplied via the $maxRebate form context.
const BannerPromotionSchema = BannerSchema.shape({
  flashPlanRateRebate: yup.percent().min(0).max(yup.ref('$maxRebate')).required().default(0),
})
type BannerPromotionSchemaType = typeof BannerPromotionSchema

// Owns the CustomBuydown key when the banner defines the buydown; releases it to children otherwise.
function reconcileOwnedFields(ownedFields: string[], bannerDefinesBuydown: boolean): string[] {
  const without = ownedFields.filter((f) => f !== CUSTOM_BUYDOWN_KEY)
  return bannerDefinesBuydown ? [...without, CUSTOM_BUYDOWN_KEY] : without
}

const BannerPromotionTabContent = ({ banner, defaults }: { banner: Merchant; defaults: BannerDto }) => {
  const { t } = useTranslation()
  const { control } = useFormContext<BannerDto>()
  useSyncFormDefaults<BannerDto>(defaults)

  const bannerDefinesBuydown = useWatch({ control, name: 'flashPlanDefinedInManageMerchant', defaultValue: false })
  const canEditRebate = (useUser()?.buyDownRebateMax ?? 0) > 0

  return (
    <Stack gap={2}>
      <Typography variant="h6">{t('banner.customBuydown.title')}</Typography>

      <ControlledRadioGroup
        control={control}
        name="flashPlanDefinedInManageMerchant"
        label={t('banner.customBuydown.modeLabel')}
        variant="card"
        options={[
          {
            value: false,
            title: t('banner.customBuydown.merchantTitle'),
            description: t('banner.customBuydown.merchantDescription'),
          },
          {
            value: true,
            title: t('banner.customBuydown.ownedTitle'),
            description: t('banner.customBuydown.ownedDescription'),
          },
        ]}
      />

      {bannerDefinesBuydown ? (
        <>
          <Divider />
          <CustomPromotionPanel id={banner.id} rebateReadOnly={!canEditRebate} />
        </>
      ) : (
        <Alert severity="info">{t('banner.customBuydown.merchantManagedNotice')}</Alert>
      )}
    </Stack>
  )
}

/**
 * Promotion tab config. `flashPlanDefinedInManageMerchant` drives everything: when true the banner
 * owns CustomBuydown and pushes one plan to every child, else ownership is released to the children.
 * `ownedFields` is reconciled from that flag on save so the key always matches it.
 */
export function useBannerPromotionTab(banner: Merchant): BannerTabConfig<BannerPromotionSchemaType> {
  const [updateBanner] = useUpdateBanner()
  const maxRebate = useUser()?.buyDownRebateMax ?? 0

  const defaults = useMemo(() => getBannerDtoFromMerchant(banner), [banner])

  const useFormSetup = useCallback(
    (): FormSetup<BannerPromotionSchemaType> => ({
      schema: BannerPromotionSchema,
      // View-only users (max 0) fall back to the stored value so an unrelated save isn't blocked.
      context: { maxRebate: maxRebate || defaults.flashPlanRateRebate },
      defaultValues: defaults,
    }),
    [defaults, maxRebate],
  )

  const onSubmit = useCallback(
    async (values: BannerDto, methods: TabFormMethods<BannerPromotionSchemaType>) => {
      const payload: BannerDto = {
        ...values,
        ownedFields: reconcileOwnedFields(values.ownedFields, values.flashPlanDefinedInManageMerchant),
      }
      await updateBanner(payload)
      methods.reset(payload)
    },
    [updateBanner],
  )

  const children = useCallback(
    () => <BannerPromotionTabContent banner={banner} defaults={defaults} />,
    [banner, defaults],
  )

  return { useFormSetup, onSubmit, children }
}
